import { ApplicationElement, html } from '../application_element/element.js';
import { classMap } from 'lit/directives/class-map.js';
import translations from './translations.js';
import style from './style.lit.css';

/**
 * Display user online status
 *
 * Each component registers user id when presented in HTML and then we use
 * periodic ajax sync (via card worker) to update online status of each id
 *
 * @prop uid - User Id
 * @prop online - Online or Offline (optional / automatic)
 * @prop label - Should show label Online/Offline (optional)
 */
export default class OnlineStatus extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  /** @internal */
  static translations = translations;

  static properties = {
    uid: { type: Number },
    online: { type: Boolean },
    label: { type: Boolean },
  };

  static all = new Set();

  static onlineIds = [];

  static updateOnlineIds(ids = []) {
    OnlineStatus.onlineIds = ids;
    document.dispatchEvent(
      new CustomEvent('online:update', { bubbles: false, cancelable: false })
    );
  }

  static clearAll() {
    OnlineStatus.all.clear();
  }

  static queryString() {
    return [...this.all].join('-');
  }

  static start() {
    document.addEventListener('sync', ({ detail }) =>
      OnlineStatus.updateOnlineIds(detail.online_users)
    );

    document.addEventListener('turbolinks:before-cache', () =>
      OnlineStatus.clearAll()
    );
  }

  constructor() {
    super();
    document.addEventListener('online:update', this);
  }

  connectedCallback() {
    super.connectedCallback();
    this.constructor.onlineIds.length && this.updateStatus();
    this.register();
  }

  get classes() {
    return {
      active: this.online,
    };
  }

  handleEvent(e) {
    e.type === 'online:update' && this.updateStatus();
  }

  disconnectCallback() {
    super.disconnectCallback();
    document.removeEventListener('online:update', this);
  }

  updateStatus() {
    this.online = this.checkStatus();
  }

  register() {
    this.constructor.all.add(this.uid);
  }

  unregister() {
    this.constructor.all.delete(this.uid);
  }

  get statusName() {
    return this.online ? this.t('online') : this.t('offline');
  }

  checkStatus() {
    return this.constructor.onlineIds.includes(this.uid);
  }

  render() {
    return html`
      <div class=${classMap(this.classes)} title=${this.statusName}>
        ${this.label ? html`<span>${this.statusName}</span>` : ''}
      </div>
    `;
  }
}

OnlineStatus.start();

window.OnlineStatus = OnlineStatus;

OnlineStatus.registerTagName('ef-online-status');

customElements.get('ef-online-status') ||
  customElements.define('ef-online-status', OnlineStatus);
